import React, {Suspense} from 'react';
import {ListConfigContext} from '@src/contexts/ListConfigContext';
import {
    ListConfigNamedColumn,
    ListTableHeadProps,
    ListTableHeadState,
    TableHeadTHPropsProvider
} from '@src/@types/components/List';
import Translate from '@src/components/Translate/Translate';
import classnames from '@src/helpers/classnames';
import RenderIf from '@src/components/RenderIf/RenderIf';
import LoadingIndicator from '@src/components/LoadingIndicator/LoadingIndicator';

const OpenListSettingsPopupButton = React.lazy(() => import('./OpenListSettingsPopupButton'));

const emptyObject = {};

export default class TableHead extends React.Component<ListTableHeadProps, ListTableHeadState> {
    static contextType = ListConfigContext;
    context!: React.ContextType<typeof ListConfigContext>;

    state: ListTableHeadState = {};

    constructor(props: any) {
        super(props);

        this.renderColumn = this.renderColumn.bind(this);
        this.state.sortBy = this.props.sortBy;
    }

    render() {
        let cellBeforeColumns: React.ReactNode = this.props.cellBeforeColumns;
        if (cellBeforeColumns && !React.isValidElement(cellBeforeColumns)) {
            const CustomComponent = cellBeforeColumns as React.ComponentType<any>;

            cellBeforeColumns = <CustomComponent/>;
        }

        return <thead {...(this.props.theadProps ?? {})}>
            <tr>
                {cellBeforeColumns}

                {this.getColumns().map(this.renderColumn)}

                <td {...(this.props.controlsTDProps ?? emptyObject)}
                    className={classnames([
                        'models-list__content-table__th',
                        'models-list__content-table__th--controls'
                    ], this.props.controlsTDProps?.className, {
                        'models-list__content-table__th--no-controls': (
                                !this.props.additionalControlNode &&
                                this.props.withoutListSettings
                        )
                    })}
                >
                    <div {...(this.props.controlsWrapperProps ?? emptyObject)}>
                        {this.props.additionalControlNode}

                        {this.props.withoutListSettings ? null : (
                                <Suspense fallback={<LoadingIndicator type="grow" size="sm"/>}>
                                    <OpenListSettingsPopupButton
                                            controllerName={
                                                    this.props.listSettingsControllerName ??
                                                    this.props.uniqueListName ??
                                                    this.context?.controllerName
                                            }
                                            refreshRecords={this.props.refreshRecords}
                                            preferenceNameMutator={this.props.preferenceNameMutator}
                                    />
                                </Suspense>
                        )}
                    </div>
                </td>
            </tr>
        </thead>;
    }

    protected getColumns() {
        return this.props.columns ?? this.context?.columns ?? [];
    }

    protected renderColumn(column: ListConfigNamedColumn, index: number) {
        const prefix = this.context?.config.columnsNameL10NPrefix ?? '';
        const label = prefix + (column.label ?? column.name);

        const sortSettings = this.state.sortBy;
        const sortedByCurrentColumn = sortSettings?.column === column.name;
        const isSortable = this.props.saveSortingParams && column.sortable;

        const propsProvider: TableHeadTHPropsProvider = typeof this.props.getTHProps === 'function' ?
                this.props.getTHProps :
                (() => this.props?.getTHProps ?? emptyObject);

        const thProps = propsProvider(column, index);

        return <th
                key={`header-${index}-column`}
                {...thProps}
                className={classnames(thProps.className, {
                    'models-list__content-table__th': true,
                    'models-list__content-table__th--sortable': column.sortable,
                })}
                onClick={() => {
                    if (!isSortable) {
                        return;
                    }

                    const direction = sortedByCurrentColumn ? (
                            sortSettings.direction === 'asc' ? 'desc' : 'asc'
                    ) : 'asc';

                    this.setState({sortBy: {column: column.name, direction}}, () => {
                        this.props.saveSortingParams?.(this.state.sortBy);
                    });
                }}
        >
            <div className="models-list__content-table__th__wrapper">
                <Translate message={label} className="models-list__content-table__th__label"/>

                <RenderIf expression={isSortable}>
                    <span className="models-list__content-table__th__sorting-indicators-wrapper">
                        {['asc', 'desc'].map((direction, i) => {
                            return <i
                                    key={`header-${index}-column--${direction}-sorting-indicator`}
                                    className={classnames({
                                        [`fa fa-caret-${i ? 'down' : 'up'}`]: true,
                                        'models-list__content-table__th__sorting-indicator': true,
                                        'models-list__content-table__th__sorting-indicator--active': (
                                                sortedByCurrentColumn && sortSettings.direction === direction
                                        )
                                    })}
                            />;
                        })}
                    </span>
                </RenderIf>
            </div>
        </th>;
    }
}
